"use client";

/**
 * "Create alert" CTA for the listings / map browse. Carries the current
 * browse filters into /alerts/new via ?prefill= so the alert starts from
 * whatever the user is looking at.
 *
 * When the current filters EXACTLY match an alert the user already has,
 * the create action is replaced by an "Alert exists" link to that alert
 * (creating a duplicate is disabled). Too-broad filters stay creatable -
 * the alert form runs that validation at save time, not here.
 *
 * Alerts are free for any signed-in user. Anonymous users still get the
 * button; the alert form handles the sign-in prompt (and applies the
 * prefill afterwards).
 */
import { useEffect, useMemo, useState } from "react";
import { Bell, CheckCircle2, Crown, X } from "lucide-react";
import type { ReadonlyURLSearchParams } from "next/navigation";
import Link from "@/lib/localized-link";
import { useAuth } from "@/lib/auth-context";
import { hasProFeatures } from "@/lib/pro";
import { useT } from "@/lib/locale-context";
import { API_BASE } from "@/lib/api";
import Overlay from "@/components/Overlay";
import {
  browseParamsToAlertFilters,
  alertFilterKey,
  encodePrefill,
} from "@/lib/filter-presets";

interface AlertSummary {
  id: number;
  filters: Record<string, unknown>;
}

// Free users get exactly ONE alert (mirrors MAX_SEARCHES_FREE in the
// backend alerts route); the second is the upgrade moment. Paid is
// effectively unlimited. We mirror the cap client-side so the button can
// pre-empt the limit with an upsell instead of letting the user fill out
// the whole form and only fail on save.
const FREE_ALERT_LIMIT = 1;

export default function CreateAlertButton({
  searchParams,
  showLabel = false,
  iconOnly = false,
  tall = false,
}: {
  searchParams: ReadonlyURLSearchParams;
  // Force the text label on (default hides it below sm). Used in the
  // mobile filter sheet footer where icon-only would be cryptic.
  showLabel?: boolean;
  // Compact square icon-only button (just the bell / check).
  iconOnly?: boolean;
  // The filter sheet's footer: as tall as the apply button beside it.
  tall?: boolean;
}) {
  const labelCls = showLabel ? "" : "hidden sm:inline";
  const { user } = useAuth();
  const t = useT();
  const [alerts, setAlerts] = useState<AlertSummary[]>([]);

  // The current browse filters as the alert form's stored blob shape.
  const blob = useMemo(
    () => browseParamsToAlertFilters(searchParams),
    [searchParams],
  );
  const myKey = useMemo(() => alertFilterKey(blob), [blob]);
  const href = `/alerts/new?prefill=${encodePrefill(blob)}`;

  // Fetch the user's alerts once (per session change) so the duplicate
  // check is a client-side compare as filters change - no refetch storm.
  useEffect(() => {
    if (!user) { setAlerts([]); return; }
    let cancelled = false;
    void fetch(`${API_BASE}/alerts`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => { if (!cancelled) setAlerts(d.items || []); })
      .catch(() => { if (!cancelled) setAlerts([]); });
    return () => { cancelled = true; };
  }, [user]);

  const dupId = useMemo(() => {
    const match = alerts.find((a) => alertFilterKey(a.filters || {}) === myKey);
    return match ? match.id : null;
  }, [alerts, myKey]);

  // Tier cap. Signed-in users at their cap (and without an exact match to
  // open) get the upsell popup instead of the create flow.
  const limit = hasProFeatures(user) ? 100 : FREE_ALERT_LIMIT;
  const atLimit = !!user && dupId == null && alerts.length >= limit;

  const [showLimit, setShowLimit] = useState(false);
  // The one-time ping ring and tooltip on this button were removed on
  // 2026-09-16 (owner: read as a generated component); the coordinator
  // that kept them apart from the saved-search mark went on 2026-10-31,
  // when coach marks joined the interruption budget (lib/floating-slot).

  const base = iconOnly
    // 38 px square: the height of the filter pills it sits beside.
    ? "inline-flex h-[38px] w-[38px] items-center justify-center rounded-lg border transition-colors"
    : tall
      ? "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors"
      : "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors";

  // Exact match exists → open that alert directly (no duplicate).
  if (dupId != null) {
    return (
      <Link
        href={`/alerts/${dupId}`}
        title={t("listings.alert.exists")}
        aria-label={iconOnly ? t("listings.alert.exists") : undefined}
        className={`${base} border-emerald-200 bg-emerald-50 text-emerald-700`}
      >
        <CheckCircle2 className="w-5 h-5 sm:w-4 sm:h-4" />
        {!iconOnly && <span className={labelCls}>{t("listings.alert.exists")}</span>}
      </Link>
    );
  }

  // Same outlined treatment as the other toolbar buttons (grey on hover too,
  // the quiet UI).
  const toolCls = "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:text-gray-900";

  // At the tier cap with no matching alert → the click is an upsell, not a
  // create. Open a popup explaining the limit + the Go-Pro / manage paths,
  // rather than sending the user into a form that will reject on save.
  if (atLimit) {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowLimit(true)}
          title={iconOnly ? t("listings.alert.create") : undefined}
          aria-label={iconOnly ? t("listings.alert.create") : undefined}
          className={`${base} ${toolCls}`}
        >
          <Bell className="w-5 h-5 sm:w-4 sm:h-4" />
          {!iconOnly && <span className={labelCls}>{t("listings.alert.create")}</span>}
        </button>

        {showLimit && (
          <Overlay
            open={showLimit}
            onClose={() => setShowLimit(false)}
            className="flex items-center justify-center p-4"
            backdropClassName="bg-black/50"
          >
            <div
              className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setShowLimit(false)}
                aria-label={t("a11y.close")}
                className="absolute right-3 top-3 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10"
              >
                <X className="h-5 w-5" />
              </button>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                {t("listings.alert.limit.title")}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {t("listings.alert.limit.body")}
              </p>
              <div className="mt-5 flex flex-col gap-2">
                <Link
                  href="/pricing"
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 transition-colors"
                >
                  <Crown className="h-4 w-4" />
                  {t("listings.alert.limit.gopro")}
                </Link>
                <Link
                  href="/alerts"
                  className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
                >
                  {t("listings.alert.limit.manage")}
                </Link>
              </div>
            </div>
          </Overlay>
        )}
      </>
    );
  }

  // Under the cap, no match → create, carrying the current browse filters.
  return (
    <div className="relative">
      <Link
        href={href}
        title={iconOnly ? t("listings.alert.create") : undefined}
        aria-label={iconOnly ? t("listings.alert.create") : undefined}
        className={`${base} ${toolCls}`}
      >
        <Bell className="w-5 h-5 sm:w-4 sm:h-4" />
        {!iconOnly && <span className={labelCls}>{t("listings.alert.create")}</span>}
      </Link>
    </div>
  );
}
